// ── Feature registry ──────────────────────────────────────────────────
//
// Single source of truth for what this app can do and who is allowed to see
// it. Consumed by BOTH the admin sidebar (AdminPage) and the role-aware
// dashboard (HomePage) — add a feature here once and it surfaces in both.
// That is the whole point: features used to be added to a screen and never
// surfaced anywhere, so nobody discovered them.
//
// Two kinds of entry:
//   adminView: true  → a view inside /admin, linked as /admin?tab=<key>
//   route: '/path'   → a standalone route
//
// `roles: null` means every signed-in role. Anything else is an explicit
// allowlist matched against the JWT role claim.

export const STAFF_ROLES = ['ADMIN', 'CURATOR', 'EMPLOYEE'];

export const FEATURE_GROUPS = [
    {
        group: 'CATALOG',
        items: [
            {
                // Matches SecurityConfig: POST/PUT/DELETE /api/products/** is
                // ADMIN and CURATOR only. EMPLOYEE used to see this card and get
                // a 403 on every action in it.
                key: 'Products', label: 'Manage Products', icon: '📦',
                roles: ['ADMIN', 'CURATOR'], adminView: true,
                description: 'Add, edit and price everything in the catalogue.',
            },
            {
                // Same roles as Products: SecurityConfig's /api/admin/catalog-imports/** rule.
                key: 'CatalogImport', label: 'Import from Catalog', icon: '📄',
                roles: ['ADMIN', 'CURATOR'], adminView: true,
                description: 'Upload a supplier PDF catalog; AI drafts the products for you to review.',
            },
            {
                key: 'Pending', label: 'Pending Approvals', icon: '⏳',
                roles: ['ADMIN', 'CURATOR'], adminView: true, badgeKey: 'pending',
                description: 'Review product submissions waiting on you.',
            },
        ],
    },
    {
        group: 'SALES',
        items: [
            {
                key: 'QuoteCart', label: 'Curated Carts', icon: '🛒',
                roles: STAFF_ROLES, adminView: true,
                description: 'Build a cart of products and send it to a customer.',
            },
            {
                // A preview tool — "see what an employee sees" — so it belongs to
                // people above an employee, not to employees themselves.
                key: 'Employee', label: 'Employee View', icon: '👤',
                roles: ['ADMIN'], adminView: true,
                description: 'See the store exactly as an employee sees it.',
            },
            // EMPLOYEE included: reps are the intended users of this screen — they
            // claim and work the leads themselves (see SecurityConfig, same widening).
            {
                key: 'Leads', label: 'Event Leads', icon: '🎯',
                roles: STAFF_ROLES, adminView: true,
                description: 'Companies with upcoming events worth contacting.',
            },
            {
                // ADMIN only, and not merely by convention: GET /api/quotes returns
                // every cart to an ADMIN but only the caller's own carts to an
                // EMPLOYEE or CURATOR. Cross-employee analytics shown to a non-admin
                // would silently be a chart of their own work, labelled as the team's.
                key: 'CartAnalytics', label: 'Cart Analytics', icon: '📉',
                roles: ['ADMIN'], adminView: true,
                description: 'Why curated carts are being lost, and who is closing them.',
            },
        ],
    },
    {
        group: 'PEOPLE',
        items: [
            {
                key: 'Users', label: 'Team Access', icon: '🔑',
                roles: ['ADMIN'], adminView: true,
                description: 'Who at eYantra can sign in, and what they can do.',
            },
            {
                key: 'TeamChats', label: 'Team Chats', icon: '💬',
                roles: ['ADMIN'], adminView: true,
                description: 'Read internal chat threads between staff.',
            },
            {
                // ADMIN only, matching SecurityConfig's /api/admin/analytics/** rule exactly.
                // Widening this to CURATOR/EMPLOYEE here would show them a tab that 403s, and
                // the reports aggregate revenue across every tenant and business line.
                key: 'Reports', label: 'Business Reports', icon: '📈',
                roles: ['ADMIN'], adminView: true,
                description: 'Scheduled email digests of how the business is doing, and who gets them.',
            },
        ],
    },
    {
        group: 'BRANDSTORE',
        items: [
            {
                // CURATOR: view-only plus order-status updates (SecurityConfig /api/admin/brandstore/**).
                key: 'Brandstore', label: 'Brandstore', icon: '🏪',
                roles: ['ADMIN', 'CURATOR'], adminView: true, badgeKey: 'brandstore',
                description: 'Tenants, orders, invoices, refunds and reward points.',
            },
        ],
    },
    {
        group: 'CONTENT',
        items: [
            {
                key: 'Gallery', label: 'Gallery', icon: '📸',
                roles: ['ADMIN', 'CURATOR'], adminView: true,
                description: 'Curate the public showcase gallery.',
            },
            {
                key: 'VisitBookings', label: 'Visit Bookings', icon: '📅',
                roles: ['ADMIN', 'CURATOR'], adminView: true,
                description: 'Experience-centre visit requests from customers.',
            },
        ],
    },
    {
        group: 'STOREFRONT',
        items: [
            {
                key: 'BrowseProducts', label: 'Browse Products', icon: '🛍️',
                roles: null, route: '/products',
                description: 'The catalogue as customers see it.',
            },
            {
                key: 'PublicGallery', label: 'Showcase Gallery', icon: '🖼️',
                roles: null, route: '/gallery',
                description: 'Past work, shareable with customers.',
            },
            {
                key: 'MyCarts', label: 'My Curated Carts', icon: '📋',
                roles: ['CUSTOMER', 'COMPANY_ADMIN', 'COMPANY_EMPLOYEE'], route: '/my-carts',
                description: 'Carts that have been shared with you.',
            },
        ],
    },
    {
        group: 'INTEGRATIONS',
        items: [
            {
                key: 'Mcp', label: 'AI Gateway (MCP)', icon: '🤖',
                roles: ['ADMIN'], route: '/mcp',
                description: 'Connect Claude, ChatGPT and Gemini to the catalogue.',
            },
        ],
    },
];

const ALL_ITEMS = FEATURE_GROUPS.flatMap(g => g.items);

/** Keys of every view that lives inside /admin — used to validate ?tab=. */
export const ADMIN_VIEW_KEYS = ALL_ITEMS.filter(i => i.adminView).map(i => i.key);

/**
 * /admin?tab=carts was the only tab param ever honoured, and Header still
 * links to it. Keep old links and bookmarks working.
 */
const LEGACY_TAB_ALIASES = { carts: 'QuoteCart' };

/** Resolve a ?tab= value to an admin view key, falling back to Products. */
export function adminViewFromParam(param) {
    if (!param) return 'Products';
    if (LEGACY_TAB_ALIASES[param]) return LEGACY_TAB_ALIASES[param];
    return ADMIN_VIEW_KEYS.includes(param) ? param : 'Products';
}

/** The link target for a feature, whichever kind it is. */
export function featureHref(item) {
    return item.adminView ? `/admin?tab=${item.key}` : item.route;
}

/**
 * The short list of links in the site header.
 *
 * Deliberately NOT the whole registry — the header is on every screen, so a
 * 14-item nav would bury the things people actually want. Staff instead get a
 * Workspace link to /dashboard, which is where the full role-filtered list
 * lives; the header's job is to make sure that door is always visible.
 *
 * Staff see their work first and the storefront second, because that's the
 * order they care about. Everyone else sees the storefront.
 *
 * `badgeKey` marks the entry that should carry a count of waiting work.
 */
export function headerNavForRole(role) {
    const isStaff = STAFF_ROLES.includes(role);
    const isSignedIn = Boolean(role);

    const storefront = [
        { key: 'products', label: 'All Products', to: '/products' },
        { key: 'gallery',  label: 'Gallery',      to: '/gallery' },
    ];

    if (isStaff) {
        return [
            { key: 'workspace', label: 'Workspace',     to: '/dashboard',      badgeKey: 'total' },
            { key: 'carts',     label: 'Curated Carts', to: '/admin?tab=carts' },
            ...storefront,
        ];
    }

    if (isSignedIn) {
        return [...storefront, { key: 'mycarts', label: 'My Curated Carts', to: '/my-carts' }];
    }

    return [...storefront, { key: 'quote', label: 'View My Quote', to: '/quote' }];
}

/** Groups filtered to what this role may see; empty groups dropped. */
export function featureGroupsForRole(role, { adminViewsOnly = false } = {}) {
    return FEATURE_GROUPS
        .map(group => ({
            ...group,
            items: group.items.filter(item =>
                (!item.roles || item.roles.includes(role)) &&
                (!adminViewsOnly || item.adminView)
            ),
        }))
        .filter(group => group.items.length > 0);
}
